// @flow
import React, { useContext, useMemo } from 'react';
import moment from 'moment-timezone';
import pipe from 'ramda/es/pipe';
import toPairs from 'ramda/es/toPairs';
import filter from 'ramda/es/filter';
import isEmpty from 'ramda/es/isEmpty';
import map from 'ramda/es/map';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useTranslation } from 'react-i18next';

import Loading from 'app/common/components/Loader';
import { Table, Tr, Td, Th } from 'app/common/ui-components/table';
import toFixedValue from 'app/common/lib/toFixedValue';
import { useCurrencySign } from 'app/common/components-next/currency';

import { SubsidiesContext, type Subsidy } from '../../../CafeteriaSubsidiesData';
import SubsidyIndicator from '../../../CafeteriaSubsidies/SubsidyIndicator';
import styles from './ItemDetailsView.module.scss';;


type SubsidyView = {
  ...$Exact<Subsidy>,
  date: string,
}

type Props = {
  subsidyOrderId: number,
  subsidyOrderNumber?: string,
}

const ItemDetailsView = ({ subsidyOrderId, subsidyOrderNumber = 'б/н' }: Props) => {
  const { t } = useTranslation('cafeteria');
  const currency = useCurrencySign('commaSpaceBefore');

  const { content: { subsidies }, loading: { subsidies: loadingSubsidies } } = useContext(SubsidiesContext);
  const loading = loadingSubsidies;

  const subsidiesByIdArray: Array<SubsidyView> = useMemo(() => pipe(
    toPairs,
    filter(([, { order_id: orderId }]: [string, Subsidy]) => orderId === subsidyOrderId),
    map(([date, props]: [string, Subsidy]) => ({ ...props, date: moment(date).format('DD.MM') })),
  )(subsidies), [subsidyOrderId, subsidies]);

  const nothing = isEmpty(subsidiesByIdArray);
  const tableHeaders = t('settings_subsidies_details_table_columns', { returnObjects: true });


  if (loading) {
    return <Loading />;
  }


  return (
    <article className={styles.root}>
      <h3 className={styles.header}>
        {nothing
          ? t('settings_subsidies_details_empty', { subsidyOrderNumber })
          : t('settings_subsidies_details_header', { subsidyOrderNumber })}
      </h3>

      {!nothing
        && <Scrollbars className={styles.scrollbars}>
          <Table className={styles.table}>
            <thead>
              <Tr className={styles.tableHeader} header>
                <Th alignLeft>
                  {tableHeaders[0]}
                </Th>
                <Th alignLeft>
                  {tableHeaders[1]}
                  {currency}
                </Th>
              </Tr>
            </thead>
            <tbody>
              {subsidiesByIdArray.map(({ order_id: id, date, total, spent }) => {
                const sTotal = total || total === 0 ? toFixedValue(total) : null;
                const sSpent = spent || spent === 0 ? toFixedValue(spent) : null;

                return (
                  <Tr key={id}>
                    <Td className={styles.cellDate}>{date}</Td>
                    <Td>
                      <SubsidyIndicator
                        available={total || 0}
                        used={spent || 0}
                      />
                      &nbsp;
                      {sTotal && sSpent && `${sSpent}/${sTotal}`}
                      {!sTotal && sSpent}
                      {!sTotal && !sSpent && '0.00'}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </Scrollbars>}
    </article>
  );
};

export default ItemDetailsView;
